import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  Download,
  Brain,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  Target,
  Zap,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Flame,
  BarChart3,
  PieChart,
  FileText,
  Users,
  UserCheck,
  ExternalLink, Eye
} from 'lucide-react';
import './InsiderTradingPage.css';

// =============================================================================
// MOCK DATA — structured to match Quiver Quantitative /beta/insiders endpoint
// Fields: Ticker, FilingDate, TradeDate, ReportedName, Title, TradeType,
//         Shares, PricePerShare, OwnedFollowingTransaction, OwnerType
// =============================================================================

const mockInsiderTrades = [
  { id: 1, ticker: 'NVDA', company: 'NVIDIA Corporation', insiderName: 'Jensen Huang', title: 'CEO & President', ownerType: 'officer', type: 'Sale', shares: 120000, pricePerShare: 142.50, totalValue: 17100000, filedDate: '2026-02-07', tradeDate: '2026-02-05', returnSince: -2.1, ownership: 'Direct' },
  { id: 2, ticker: 'AAPL', company: 'Apple Inc.', insiderName: 'Tim Cook', title: 'CEO', ownerType: 'officer', type: 'Sale', shares: 75000, pricePerShare: 238.20, totalValue: 17865000, filedDate: '2026-02-06', tradeDate: '2026-02-03', returnSince: 1.3, ownership: 'Direct' },
  { id: 3, ticker: 'LUMN', company: 'Lumen Technologies', insiderName: 'Kate Johnson', title: 'President & CEO', ownerType: 'officer', type: 'Purchase', shares: 78685, pricePerShare: 7.82, totalValue: 615115, filedDate: '2026-02-05', tradeDate: '2026-02-03', returnSince: 8.7, ownership: 'Direct' },
  { id: 4, ticker: 'DXC', company: 'DXC Technology', insiderName: 'Raul Fernandez', title: 'President & CEO', ownerType: 'officer', type: 'Purchase', shares: 16446, pricePerShare: 15.24, totalValue: 250573, filedDate: '2026-02-05', tradeDate: '2026-02-02', returnSince: 5.2, ownership: 'Direct' },
  { id: 5, ticker: 'MNRO', company: 'Monro Inc.', insiderName: 'Michael Broderick', title: 'President & CEO', ownerType: 'officer', type: 'Purchase', shares: 13350, pricePerShare: 18.96, totalValue: 253116, filedDate: '2026-02-04', tradeDate: '2026-02-01', returnSince: 3.8, ownership: 'Direct' },
  { id: 6, ticker: 'CVCO', company: 'Cavco Industries', insiderName: 'William Boor', title: 'President & CEO', ownerType: 'officer', type: 'Purchase', shares: 1000, pricePerShare: 352.40, totalValue: 352400, filedDate: '2026-02-04', tradeDate: '2026-01-31', returnSince: 6.1, ownership: 'Direct' },
  { id: 7, ticker: 'TSLA', company: 'Tesla Inc.', insiderName: 'Vaibhav Taneja', title: 'CFO', ownerType: 'officer', type: 'Sale', shares: 25000, pricePerShare: 394.80, totalValue: 9870000, filedDate: '2026-02-03', tradeDate: '2026-01-30', returnSince: -4.3, ownership: 'Direct' },
  { id: 8, ticker: 'META', company: 'Meta Platforms', insiderName: 'Javier Olivan', title: 'COO', ownerType: 'officer', type: 'Sale', shares: 8200, pricePerShare: 612.30, totalValue: 5020860, filedDate: '2026-02-03', tradeDate: '2026-01-29', returnSince: 2.8, ownership: 'Direct' },
  { id: 9, ticker: 'JPM', company: 'JPMorgan Chase', insiderName: 'Mary Erdoes', title: 'CEO Asset & Wealth Mgmt', ownerType: 'officer', type: 'Purchase', shares: 5000, pricePerShare: 248.10, totalValue: 1240500, filedDate: '2026-02-02', tradeDate: '2026-01-28', returnSince: 4.5, ownership: 'Direct' },
  { id: 10, ticker: 'AMZN', company: 'Amazon.com Inc.', insiderName: 'Andrew Jassy', title: 'CEO', ownerType: 'officer', type: 'Sale', shares: 50000, pricePerShare: 228.40, totalValue: 11420000, filedDate: '2026-02-01', tradeDate: '2026-01-27', returnSince: 1.1, ownership: 'Direct' },
  { id: 11, ticker: 'PLTR', company: 'Palantir Technologies', insiderName: 'Alexander Karp', title: 'CEO', ownerType: 'officer', type: 'Sale', shares: 350000, pricePerShare: 82.60, totalValue: 28910000, filedDate: '2026-01-31', tradeDate: '2026-01-26', returnSince: -6.2, ownership: 'Direct' },
  { id: 12, ticker: 'COST', company: 'Costco Wholesale', insiderName: 'Ron Vachris', title: 'President & CEO', ownerType: 'officer', type: 'Purchase', shares: 2500, pricePerShare: 945.30, totalValue: 2363250, filedDate: '2026-01-30', tradeDate: '2026-01-25', returnSince: 2.9, ownership: 'Direct' },
  { id: 13, ticker: 'NFLX', company: 'Netflix Inc.', insiderName: 'Ann Mather', title: 'Director', ownerType: 'director', type: 'Sale', shares: 1500, pricePerShare: 948.20, totalValue: 1422300, filedDate: '2026-01-29', tradeDate: '2026-01-24', returnSince: 0.8, ownership: 'Direct' },
  { id: 14, ticker: 'WMT', company: 'Walmart Inc.', insiderName: 'John Furner', title: 'President & CEO, Walmart U.S.', ownerType: 'officer', type: 'Purchase', shares: 12000, pricePerShare: 92.40, totalValue: 1108800, filedDate: '2026-01-28', tradeDate: '2026-01-23', returnSince: 3.4, ownership: 'Direct' },
  { id: 15, ticker: 'V', company: 'Visa Inc.', insiderName: 'Ryan McInerney', title: 'CEO', ownerType: 'officer', type: 'Sale', shares: 18000, pricePerShare: 318.70, totalValue: 5736600, filedDate: '2026-01-27', tradeDate: '2026-01-22', returnSince: 1.6, ownership: 'Direct' },
  { id: 16, ticker: 'OXY', company: 'Occidental Petroleum', insiderName: 'Vicki Hollub', title: 'President & CEO', ownerType: 'officer', type: 'Purchase', shares: 28000, pricePerShare: 48.90, totalValue: 1369200, filedDate: '2026-01-26', tradeDate: '2026-01-21', returnSince: 7.2, ownership: 'Direct' },
  { id: 17, ticker: 'CRM', company: 'Salesforce Inc.', insiderName: 'Parker Harris', title: 'Director & Co-Founder', ownerType: 'director', type: 'Sale', shares: 22000, pricePerShare: 342.10, totalValue: 7526200, filedDate: '2026-01-25', tradeDate: '2026-01-20', returnSince: -1.4, ownership: 'Indirect' },
  { id: 18, ticker: 'LLY', company: 'Eli Lilly & Co.', insiderName: 'David Ricks', title: 'Chairman & CEO', ownerType: 'officer', type: 'Purchase', shares: 3200, pricePerShare: 812.50, totalValue: 2600000, filedDate: '2026-01-24', tradeDate: '2026-01-19', returnSince: 4.1, ownership: 'Direct' },
  { id: 19, ticker: 'UBER', company: 'Uber Technologies', insiderName: 'Nelson Chai', title: '10% Owner', ownerType: 'tenPercent', type: 'Sale', shares: 500000, pricePerShare: 78.40, totalValue: 39200000, filedDate: '2026-01-23', tradeDate: '2026-01-18', returnSince: -3.1, ownership: 'Indirect' },
  { id: 20, ticker: 'HD', company: 'Home Depot Inc.', insiderName: 'Ted Decker', title: 'Chairman, President & CEO', ownerType: 'officer', type: 'Purchase', shares: 4500, pricePerShare: 402.80, totalValue: 1812600, filedDate: '2026-01-22', tradeDate: '2026-01-17', returnSince: 2.3, ownership: 'Direct' },
];

const mockClusterBuys = [
  { ticker: 'LUMN', company: 'Lumen Technologies', insiderCount: 4, totalValue: 2100000, avgReturn: 8.7, timeframe: '10 days' },
  { ticker: 'OXY', company: 'Occidental Petroleum', insiderCount: 3, totalValue: 4200000, avgReturn: 7.2, timeframe: '14 days' },
  { ticker: 'JPM', company: 'JPMorgan Chase', insiderCount: 3, totalValue: 3800000, avgReturn: 4.5, timeframe: '21 days' },
  { ticker: 'DXC', company: 'DXC Technology', insiderCount: 2, totalValue: 980000, avgReturn: 5.2, timeframe: '7 days' },
];

const mockTopBuyers = [
  { name: 'Kate Johnson', title: 'CEO, LUMN', value: '$615K', returnSince: '+8.7%' },
  { name: 'Vicki Hollub', title: 'CEO, OXY', value: '$1.37M', returnSince: '+7.2%' },
  { name: 'William Boor', title: 'CEO, CVCO', value: '$352K', returnSince: '+6.1%' },
  { name: 'Raul Fernandez', title: 'CEO, DXC', value: '$251K', returnSince: '+5.2%' },
  { name: 'Mary Erdoes', title: 'CEO AWM, JPM', value: '$1.24M', returnSince: '+4.5%' },
];

const mockSectorBreakdown = [
  { name: 'Technology', pct: 38, color: '#8b5cf6' },
  { name: 'Finance', pct: 22, color: '#3b82f6' },
  { name: 'Healthcare', pct: 16, color: '#10b981' },
  { name: 'Energy', pct: 12, color: '#f59e0b' },
  { name: 'Consumer', pct: 8, color: '#ec4899' },
  { name: 'Other', pct: 4, color: '#6b7280' },
];

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (value) => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatShares = (num) => {
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M shares`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(0)}K shares`;
  return `${num.toLocaleString()} shares`;
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const StatCard = ({ icon: Icon, label, value, sub, subType, color }) => (
  <div className="it-stat-card">
    <div className="it-stat-icon" style={{ background: color }}>
      <Icon size={18} color="#fff" />
    </div>
    <div className="it-stat-label">{label}</div>
    <div className="it-stat-value">{value}</div>
    <div className={`it-stat-sub ${subType}`}>{sub}</div>
  </div>
);

const AIInsightCard = ({ insight }) => (
  <div className="it-ai-insight">
    <div className="it-ai-insight-header">
      <span className="it-ai-badge"><Brain size={12} /> AI INSIGHT</span>
      <span>Updated 2 hours ago</span>
    </div>
    <p dangerouslySetInnerHTML={{ __html: insight }} />
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const InsiderTradingPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Filtered + sorted data
  const filteredTrades = useMemo(() => {
    let result = [...mockInsiderTrades];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.ticker.toLowerCase().includes(q) ||
        t.company.toLowerCase().includes(q) ||
        t.insiderName.toLowerCase().includes(q) ||
        t.title.toLowerCase().includes(q)
      );
    }

    // Buy/Sell filter
    if (filterType === 'buys') result = result.filter(t => t.type === 'Purchase');
    if (filterType === 'sells') result = result.filter(t => t.type === 'Sale');

    // Role filter
    if (filterRole === 'officer') result = result.filter(t => t.ownerType === 'officer');
    if (filterRole === 'director') result = result.filter(t => t.ownerType === 'director');
    if (filterRole === 'tenPercent') result = result.filter(t => t.ownerType === 'tenPercent');

    // Sort
    result.sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortBy === 'value') return order * (a.totalValue - b.totalValue);
      if (sortBy === 'date') return order * (new Date(a.filedDate) - new Date(b.filedDate));
      if (sortBy === 'return') return order * (a.returnSince - b.returnSince);
      if (sortBy === 'shares') return order * (a.shares - b.shares);
      return 0;
    });

    return result;
  }, [searchQuery, filterType, filterRole, sortBy, sortOrder]);

  // Compute stats from data
  const stats = useMemo(() => {
    const buys = mockInsiderTrades.filter(t => t.type === 'Purchase');
    const sells = mockInsiderTrades.filter(t => t.type === 'Sale');
    const buyVolume = buys.reduce((sum, t) => sum + t.totalValue, 0);
    const sellVolume = sells.reduce((sum, t) => sum + t.totalValue, 0);
    const buyCount = buys.length;
    const sellCount = sells.length;
    const ratio = sellCount > 0 ? (buyCount / sellCount).toFixed(1) : buyCount;

    return { buyVolume, sellVolume, buyCount, sellCount, ratio, total: mockInsiderTrades.length };
  }, []);

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const aiInsight = `<span class="highlight">Insider Buy Cluster Detected:</span> 4 executives at <span class="highlight">LUMN</span> purchased shares totaling $2.1M over the past 10 days — the strongest insider cluster signal this month. CEO Kate Johnson alone bought 78,685 shares. Meanwhile, notable sells continue from tech CEOs: Karp (PLTR) sold $28.9M and Jassy (AMZN) sold $11.4M in scheduled dispositions. <span class="highlight">Buy/sell ratio sits at ${stats.ratio}:1</span>, tilting bullish. Insider buying concentrated in <span class="highlight">Energy</span> and <span class="highlight">Financials</span> while selling dominated by large-cap Tech.`;

  return (
    <div className="insider-trading-page">
      {/* Header */}
      <div className="it-page-header">
        <div className="it-header-content">
          <div className="it-header-title">
            <div className="it-header-icon">
              <FileText size={22} color="#fff" />
            </div>
            <div>
              <h1>Insider Trading</h1>
              <p>SEC Form 4 filings • Corporate officer, director & 10% owner transactions</p>
            </div>
          </div>
          <div className="it-header-actions">
            <button className="it-header-btn"><Download size={14} /> Export</button>
            <button className="it-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <AIInsightCard insight={aiInsight} />

      {/* Stats Row */}
      <div className="it-stats-row">
        <StatCard
          icon={TrendingUp}
          label="Insider Buy Volume (30d)"
          value={formatCurrency(stats.buyVolume)}
          sub={`${stats.buyCount} transactions`}
          subType="positive"
          color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))"
        />
        <StatCard
          icon={TrendingDown}
          label="Insider Sell Volume (30d)"
          value={formatCurrency(stats.sellVolume)}
          sub={`${stats.sellCount} transactions`}
          subType="negative"
          color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))"
        />
        <StatCard
          icon={Target}
          label="Cluster Buy Signals"
          value={mockClusterBuys.length.toString()}
          sub="Multiple insiders buying"
          subType="neutral"
          color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))"
        />
        <StatCard
          icon={BarChart3}
          label="Buy / Sell Ratio"
          value={`${stats.ratio}:1`}
          sub={stats.ratio >= 1 ? 'Bullish signal' : 'Bearish signal'}
          subType={stats.ratio >= 1 ? 'positive' : 'negative'}
          color="linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))"
        />
      </div>

      {/* Content Grid */}
      <div className="it-content-grid">
        {/* Main Table Panel */}
        <div className="it-table-panel">
          {/* Panel Header */}
          <div className="it-panel-header">
            <h2>
              <FileText size={16} />
              Recent Form 4 Filings
              <span className="it-record-count">{filteredTrades.length}</span>
            </h2>
          </div>

          {/* Toolbar */}
          <div className="it-toolbar">
            <div className="it-search-box">
              <Search size={14} color="rgba(255,255,255,0.3)" />
              <input
                type="text"
                placeholder="Search stock, insider, or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="it-filter-toggles">
              <button
                className={`it-filter-btn ${filterType === 'all' ? 'active' : ''}`}
                onClick={() => setFilterType('all')}
              >All</button>
              <button
                className={`it-filter-btn buy ${filterType === 'buys' ? 'active' : ''}`}
                onClick={() => setFilterType('buys')}
              >Buys</button>
              <button
                className={`it-filter-btn sell ${filterType === 'sells' ? 'active' : ''}`}
                onClick={() => setFilterType('sells')}
              >Sells</button>
            </div>
            <div className="it-role-filters">
              <button
                className={`it-role-btn ${filterRole === 'all' ? 'active' : ''}`}
                onClick={() => setFilterRole('all')}
              >All Roles</button>
              <button
                className={`it-role-btn ${filterRole === 'officer' ? 'active' : ''}`}
                onClick={() => setFilterRole('officer')}
              >Officers</button>
              <button
                className={`it-role-btn ${filterRole === 'director' ? 'active' : ''}`}
                onClick={() => setFilterRole('director')}
              >Directors</button>
              <button
                className={`it-role-btn ${filterRole === 'tenPercent' ? 'active' : ''}`}
                onClick={() => setFilterRole('tenPercent')}
              >10% Owners</button>
            </div>
          </div>

          {/* Table */}
          {filteredTrades.length === 0 ? (
            <div className="it-empty-state">
              <Search size={40} />
              <p>No insider trades match your filters</p>
            </div>
          ) : (
            <div className="it-table-wrap">
              <table className="it-table">
                <thead>
                  <tr>
                    <th>Stock</th>
                    <th>Insider</th>
                    <th>Type</th>
                    <th className={sortBy === 'value' ? 'sorted' : ''} onClick={() => handleSort('value')}>
                      Value <SortIcon field="value" />
                    </th>
                    <th className={sortBy === 'shares' ? 'sorted' : ''} onClick={() => handleSort('shares')}>
                      Shares <SortIcon field="shares" />
                    </th>
                    <th className={sortBy === 'date' ? 'sorted' : ''} onClick={() => handleSort('date')}>
                      Filed <SortIcon field="date" />
                    </th>
                    <th className={sortBy === 'return' ? 'sorted' : ''} onClick={() => handleSort('return')}>
                      Return <SortIcon field="return" />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrades.map(trade => {
                    const insiderSlug = trade.insiderName.toLowerCase().replace(/\s+/g, '-');
                    return (
                    <tr key={trade.id} onClick={() => navigate(`/insider/${insiderSlug}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div className="it-stock-cell" onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                          <span className="it-stock-symbol">{trade.ticker}</span>
                          <span className="it-stock-name">{trade.company}</span>
                        </div>
                      </td>
                      <td>
                        <div className="it-insider-cell">
                          <span className="it-insider-name">{trade.insiderName}</span>
                          <span className="it-insider-title">{trade.title}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`it-type-badge ${trade.type === 'Purchase' ? 'buy' : 'sell'}`}>
                          {trade.type === 'Purchase' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                          {trade.type === 'Purchase' ? 'Buy' : 'Sell'}
                        </span>
                      </td>
                      <td>
                        <div>
                          <span className="it-amount">{formatCurrency(trade.totalValue)}</span>
                          <br />
                          <span className="it-price">@ ${trade.pricePerShare.toFixed(2)}</span>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span className="it-amount">{formatShares(trade.shares)}</span>
                          <br />
                          <span className="it-ownership-badge">{trade.ownership}</span>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span className="it-date">{formatDate(trade.filedDate)}</span>
                          <br />
                          <span className="it-price">Traded {formatDate(trade.tradeDate)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`it-return ${trade.returnSince >= 0 ? 'positive' : 'negative'}`}>
                          {trade.returnSince >= 0 ? '+' : ''}{trade.returnSince}%
                        </span>
                      </td>
                      <td>
                        <button
                          className="it-stock-link-btn"
                          onClick={(e) => { e.stopPropagation(); navigate(`/stock/${trade.ticker}?tab=insiders`); }}
                          title={`View ${trade.ticker} insider activity`}
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="it-pagination">
            <span className="it-pagination-info">
              Showing {filteredTrades.length} of {mockInsiderTrades.length} filings
            </span>
            <div className="it-pagination-btns">
              <button className="it-page-btn" disabled><ChevronLeft size={14} /></button>
              <button className="it-page-btn active">1</button>
              <button className="it-page-btn">2</button>
              <button className="it-page-btn">3</button>
              <button className="it-page-btn"><ChevronRight size={14} /></button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="it-sidebar">
          {/* Buy/Sell Ratio */}
          <div className="it-sidebar-card">
            <div className="it-card-header">
              <h3><BarChart3 size={16} color="#667eea" /> Buy vs Sell (30d)</h3>
            </div>
            <div className="it-ratio-chart">
              <div className="it-ratio-bar">
                <div className="it-ratio-buy" style={{ width: `${(stats.buyCount / stats.total) * 100}%` }} />
                <div className="it-ratio-sell" style={{ width: `${(stats.sellCount / stats.total) * 100}%` }} />
              </div>
              <div className="it-ratio-labels">
                <span className="it-ratio-label">
                  <span className="dot buy" />
                  <span className="label-text">Buys</span>
                  <span className="label-value">{stats.buyCount} ({formatCurrency(stats.buyVolume)})</span>
                </span>
                <span className="it-ratio-label">
                  <span className="dot sell" />
                  <span className="label-text">Sells</span>
                  <span className="label-value">{stats.sellCount} ({formatCurrency(stats.sellVolume)})</span>
                </span>
              </div>
            </div>
          </div>

          {/* Top Insider Buyers */}
          <div className="it-sidebar-card">
            <div className="it-card-header">
              <h3><Flame size={16} color="#f59e0b" /> Top Insider Buyers</h3>
              <button className="it-card-link">View All</button>
            </div>
            <div className="it-leaderboard">
              {mockTopBuyers.map((buyer, i) => (
                <div className="it-leader-row" key={i}>
                  <span className={`it-leader-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : ''}`}>
                    {i + 1}
                  </span>
                  <div className="it-leader-info">
                    <div className="it-leader-name">{buyer.name}</div>
                    <div className="it-leader-meta">{buyer.title} • {buyer.value}</div>
                  </div>
                  <span className="it-leader-value">{buyer.returnSince}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cluster Buy Alerts */}
          <div className="it-sidebar-card">
            <div className="it-card-header">
              <h3><Zap size={16} color="#f59e0b" /> Cluster Buy Alerts</h3>
              <button className="it-card-link">Details</button>
            </div>
            <div className="it-cluster-list">
              {mockClusterBuys.map((cluster, i) => (
                <div className="it-cluster-item" key={i}>
                  <div className="it-cluster-icon">
                    <Users size={16} color="#f59e0b" />
                  </div>
                  <div className="it-cluster-info">
                    <div className="it-cluster-ticker">
                      {cluster.ticker}
                      <span style={{ fontWeight: 400, fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', marginLeft: 6 }}>
                        {cluster.insiderCount} insiders • {cluster.timeframe}
                      </span>
                    </div>
                    <div className="it-cluster-detail">
                      {formatCurrency(cluster.totalValue)} total purchases
                    </div>
                  </div>
                  <span className={`it-cluster-return ${cluster.avgReturn >= 0 ? 'positive' : 'negative'}`}>
                    +{cluster.avgReturn}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sector Breakdown */}
          <div className="it-sidebar-card">
            <div className="it-card-header">
              <h3><PieChart size={16} color="#667eea" /> Insider Activity by Sector</h3>
            </div>
            <div className="it-sector-list">
              {mockSectorBreakdown.map((sector, i) => (
                <div className="it-sector-row" key={i}>
                  <span className="it-sector-name">{sector.name}</span>
                  <div className="it-sector-bar-wrap">
                    <div
                      className="it-sector-bar-fill"
                      style={{ width: `${sector.pct}%`, background: sector.color }}
                    />
                  </div>
                  <span className="it-sector-pct">{sector.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsiderTradingPage;